import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import { ControlledDropdown } from "../components/form/controlled-dropdown.tsx";
import FileUpload from "../components/form/file-upload.tsx";
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toaster } from "../components/ui/toaster.tsx";
import { useUpload } from "../utils/hooks/use-upload.ts";
import useUserStore from "../stores/user.store.ts";
import { useNavigate, useParams } from "react-router-dom";
import {
  CreateWarehouse,
  createWarehouseSchema,
} from "../schemas/create-warehouse.schema.ts";
import { api } from "../api/custom-api.ts";
import { CreateCodebookRequest } from "@ns/api";
import { PlateTypeEnum } from "@ns/types";
import { LocationSelectProps } from "../utils/types/location.props.ts";
import Loader from "../components/loader.tsx";

const CreateWarehouseForm = () => {
  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = useFormContext<CreateWarehouse>();

  const { id } = useParams();

  const [codebookFiles, setCodebookFiles] = useState<string[]>([]);

  const { data } = useQuery({
    queryKey: ["codebookWarehouse", id!],
    queryFn: () => api.codebookWarehouse.getCodebookWarehouseById(id!),
    enabled: !!id,
  });

  const { upload, isProgress } = useUpload();

  const navigate = useNavigate();

  const { setIsDeletedFiles } = useUserStore();

  const handleUploadCodebook = async (file: File) => {
    const formData: FormData = new FormData();
    formData.append("file", file);

    upload(formData)
      .then((response: string) => {
        setCodebookFiles((prevState) => [...prevState, response]);
      })
      .catch((error: unknown) => {
        console.log((error as Error).message);
      });
  };

  const resetFields = () => {
    reset({
      width: "",
      height: "",
      thickness: "",
      price: "",
      quantity: "",
      location: [""],
    });
    setCodebookFiles([]);
    setIsDeletedFiles(true);
  };

  useEffect(() => {
    resetFields();
    setIsDeletedFiles(false);
  }, []);

  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: (request: CreateCodebookRequest) =>
      api.codebook.createCodebook(request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi nalog!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Novi nalog je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      resetFields();
      queryClient.invalidateQueries({ queryKey: ["codebook"] });
      navigate("/warehouses");
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutate({
      codebookWarehouseId: id!,
      codebookFiles: codebookFiles,
      width: data.width,
      height: data.height,
      thickness: data.thickness,
      price: Number(data.price),
      quantity: Number(data.quantity),
      location: data.location[0],
    });
  });

  if (isLoading || isProgress) {
    return <Loader />;
  }

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className={"text-[24px] text-black font-semibold"}>
            DODAJ PROIZVOD NA LAGER
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isValid}
              onClick={onSubmit}
            >
              <IoCheckmark /> Potvrdi
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <div
            className={
              "flex flex-row gap-[1rem] md:gap-[2.1rem] justify-between px-[0.25rem]"
            }
          >
            <ControlledInput
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              name={"width"}
              label={"Širina (cm1)"}
              placeholder={"(cm1)"}
            />
            <ControlledInput
              name={"height"}
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              label={"Dužina (cm1)"}
              placeholder={"(cm1)"}
            />
            <ControlledInput
              name={"thickness"}
              className={"w-[27%] sm:w-[5rem] md:w-[8.5rem]"}
              label={"Debljina (cm1)"}
              placeholder={"(cm1)"}
            />
          </div>
          {data?.plateType &&
            data.plateType === PlateTypeEnum.COMMERCIAL_STONE && (
              <ControlledInput
                className="w-full sm:w-[24rem] md:w-[28rem]"
                name={"price"}
                placeholder={"Unesi cenu materijala (EUR/m2)"}
                label={"Cena materijala (EUR/m2)"}
              />
            )}
          {data?.plateType && data.plateType === PlateTypeEnum.TABLE && (
            <ControlledInput
              className="w-full sm:w-[24rem] md:w-[28rem]"
              name={"price"}
              placeholder={"Unesi cenu materijala (EUR/tabla)"}
              label={"Cena materijala (EUR/tabla)"}
            />
          )}
          <ControlledDropdown
            name={"location"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            options={LocationSelectProps}
            placeholder={"Izaberi lokaciju"}
            label={"Lokacija"}
          />
          <ControlledInput
            className="w-full sm:w-[24rem] md:w-[28rem]"
            name={"quantity"}
            placeholder={"Unesi količinu"}
            label={"Količina"}
          />
          <FileUpload
            maxFiles={10}
            label={"Fotografije materijala"}
            onUpload={handleUploadCodebook}
            files={[]}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateWarehousePage = () => {
  return (
    <ControlledForm schema={createWarehouseSchema}>
      <CreateWarehouseForm />
    </ControlledForm>
  );
};
